import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router";
import { Box, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useSnackbar } from "notistack";

import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import InsurerSettingsForm from "./InsurerSettingsForm.jsx";
import InsurerContactsTable from "./InsurerContactsTable.jsx";

function MasterDataItem({ label, value }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography>{value || "–"}</Typography>
    </Box>
  );
}

export default function InsurerShowPage() {
  const { id } = useParams();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();

  const [insurer, setInsurer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState("1");

  const fetchData = useCallback(async () => {
    try {
      await backendGet(`data/insurers/${id}`, {
        label: "Versicherer",
        enqueueSnackbar,
        setResult: (res) => setInsurer(res.data),
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayName = insurer?.name || `Versicherer #${id}`;

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Versicherungsgesellschaften", path: "/admin/insurers" },
      { name: displayName, path: `/admin/insurers/${id}` },
    ]);
  }, [setBreadcrumbs, displayName, id]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!insurer) {
    return (
      <Box>
        <Typography variant="h1" gutterBottom>
          Versicherer
        </Typography>
        <Typography variant="h3" sx={{ mb: 3 }}>
          Versicherer nicht gefunden
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" gutterBottom>
          {displayName}
        </Typography>
      </Box>

      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={(event, value) => setTabValue(value)}
              aria-label="Versicherer Tabs"
            >
              <Tab label="Stammdaten" value="1" />
              <Tab label="Kontakte" value="2" />
              <Tab label="Einstellungen" value="3" />
            </TabList>
          </Box>
          <TabPanel value="1" sx={{ paddingX: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <MasterDataItem label="Name" value={insurer.name} />
              <MasterDataItem label="Agenturnummer" value={insurer.agenturnummer} />
              <MasterDataItem
                label="Standard-Schadenmailadresse"
                value={insurer.default_contact?.email}
              />
            </Box>
          </TabPanel>
          <TabPanel value="2" sx={{ paddingX: 0 }}>
            <InsurerContactsTable
              insurerId={insurer.id}
              onDefaultChanged={fetchData}
            />
          </TabPanel>
          <TabPanel value="3" sx={{ paddingX: 0 }}>
            <InsurerSettingsForm insurer={insurer} onSaved={fetchData} />
          </TabPanel>
        </TabContext>
      </Box>
    </Box>
  );
}
